function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]);

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { nanoid } from "nanoid";
import useField from "../hooks/internal/useField";
import useBackground from "../hooks/useBackground";
import useResponsivePropsCSS from "../hooks/useResponsivePropsCSS";
import { responsiveHeightType } from "../hooks/useResponsiveProp";
import { responsiveSize } from "../utils/css";
import { mergeProps } from "../utils/component";
import Field from "./internal/Field";
import { jsx as ___EmotionJSX } from "@emotion/core";
var COLORS = ["grey.t05", "white"];
var DEFAULT_PROPS = {
  color: "grey.t05",
  disabled: false,
  optional: false,
  validate: function validate(value, _ref) {
    var isEmpty = _ref.isEmpty;

    if (isEmpty(value)) {
      return "Required";
    }

    return null;
  },
  __internal__focus: false
};
Textarea.COLORS = COLORS;
Textarea.DEFAULT_PROPS = DEFAULT_PROPS;

function Textarea(props) {
  var mergedProps = mergeProps(props, DEFAULT_PROPS, {}, {
    maxLength: function maxLength(_maxLength) {
      return typeof _maxLength === "string" || typeof _maxLength === "number";
    },
    color: function color(_color) {
      return COLORS.includes(_color);
    },
    disabled: function disabled(_disabled) {
      return typeof _disabled === "boolean";
    },
    optional: function optional(_optional) {
      return typeof _optional === "boolean";
    }
  });
  var name = mergedProps.name,
      maxLength = mergedProps.maxLength,
      label = mergedProps.label,
      placeholder = mergedProps.placeholder,
      helpText = mergedProps.helpText,
      disabled = mergedProps.disabled,
      optional = mergedProps.optional,
      validate = mergedProps.validate,
      validateData = mergedProps.validateData,
      onChangeProp = mergedProps.onChange,
      testId = mergedProps.testId,
      __internal__focus = mergedProps.__internal__focus;

  var _useState = useState(function () {
    return "textarea-".concat(nanoid());
  }),
      _useState2 = _slicedToArray(_useState, 1),
      textareaId = _useState2[0];

  var _useState3 = useState(function () {
    return "textarea-aux-".concat(nanoid());
  }),
      _useState4 = _slicedToArray(_useState3, 1),
      auxId = _useState4[0];

  var isEmpty = useCallback(function (value) {
    return value.trim() === "";
  }, []);
  var data = useMemo(function () {
    return _objectSpread({
      isEmpty: isEmpty
    }, validateData && {
      data: validateData
    });
  }, [isEmpty, validateData]);

  var _useField = useField("Textarea", {
    name: name,
    disabled: disabled,
    optional: optional,
    validate: validate,
    data: data
  }),
      value = _useField.value,
      errors = _useField.errors,
      hasErrors = _useField.hasErrors,
      onFocus = _useField.onFocus,
      onBlur = _useField.onBlur,
      fieldOnChange = _useField.onChange;

  var onChange = useCallback(function (event) {
    fieldOnChange(event);
    onChangeProp && onChangeProp({
      value: event.target.value
    });
  }, [fieldOnChange, onChangeProp]);

  var _useBackground = useBackground(),
      inputColorMap = _useBackground.inputColorMap;

  var textareaCSS = useResponsivePropsCSS(props, DEFAULT_PROPS, {
    color: function color(propsAtBreakpoint, theme, bp) {
      var _props$color;

      var color = (_props$color = props.color) !== null && _props$color !== void 0 ? _props$color : inputColorMap[bp];
      return theme.textarea.getCSS({
        color: color,
        __internal__focus: __internal__focus
      });
    },
    height: responsiveSize("height")
  });
  return ___EmotionJSX(Field, {
    optional: optional,
    disabled: disabled,
    label: label,
    labelFor: textareaId,
    auxId: auxId,
    helpText: helpText,
    errors: errors,
    testId: testId
  }, ___EmotionJSX("textarea", {
    css: textareaCSS,
    id: textareaId,
    name: name,
    placeholder: placeholder,
    maxLength: maxLength,
    disabled: disabled,
    autoComplete: "off",
    autoCorrect: "off",
    autoCapitalize: "off",
    spellCheck: "false",
    "aria-invalid": hasErrors ? "true" : null,
    "aria-describedby": helpText || hasErrors ? auxId : null,
    onFocus: onFocus,
    onBlur: onBlur,
    value: value,
    onChange: onChange
  }));
}

Textarea.propTypes = _objectSpread(_objectSpread({
  name: PropTypes.string.isRequired,
  maxLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.oneOf(COLORS),
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  helpText: PropTypes.node,
  disabled: PropTypes.bool,
  optional: PropTypes.bool
}, responsiveHeightType), {}, {
  validate: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  validateData: PropTypes.any,
  onChange: PropTypes.func,
  testId: PropTypes.string,
  __internal__focus: PropTypes.bool
});
export default Textarea;